import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Skeleton,
  Alert,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { monitoramentoService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import DateRangeSelector from '../../components/monitoramento/DateRangeSelector';
import KpiCard from '../../components/monitoramento/KpiCard';
import { ChatIAButton } from '../../components/ChatIA';
import AnalisePredicaoCard from '../../components/saude/AnalisePredicaoCard';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import BusinessIcon from '@mui/icons-material/Business';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import HotelIcon from '@mui/icons-material/Hotel';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AssignmentIcon from '@mui/icons-material/Assignment';

const toISO = (d: Date): string => d.toISOString().slice(0, 10);

const SUB_PANELS = [
  { label: 'Leitos Hospitalares', path: '/monitoramento/leitos', icon: <HotelIcon sx={{ fontSize: 32 }} />, color: '#007AF0' },
  { label: 'Internacoes', path: '/monitoramento/internacoes', icon: <MeetingRoomIcon sx={{ fontSize: 32 }} />, color: '#02786D' },
  { label: 'Tempos de Espera', path: '/monitoramento/tempos-espera', icon: <AccessTimeIcon sx={{ fontSize: 32 }} />, color: '#E76F00' },
  { label: 'Regulacao', path: '/monitoramento/regulacao', icon: <AssignmentIcon sx={{ fontSize: 32 }} />, color: '#6a1b9a' },
];

const MonitoramentoDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { municipioAtivo } = useAuth();

  const [data, setData] = useState<any>(null);
  const [evolucao, setEvolucao] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const hoje = new Date();
  const tresMesesAtras = new Date();
  tresMesesAtras.setMonth(tresMesesAtras.getMonth() - 3);
  const [dataInicio, setDataInicio] = useState(toISO(tresMesesAtras));
  const [dataFim, setDataFim] = useState(toISO(hoje));

  const handleDateChange = (di: string, df: string) => {
    setDataInicio(di);
    setDataFim(df);
  };

  useEffect(() => {
    if (!municipioAtivo?.id) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [visaoGeral, evolucaoData] = await Promise.all([
          monitoramentoService.getVisaoGeral(municipioAtivo.id, dataInicio, dataFim),
          monitoramentoService.getVisaoGeralEvolucao(municipioAtivo.id),
        ]);

        setData(visaoGeral);
        setEvolucao(Array.isArray(evolucaoData) ? evolucaoData : evolucaoData?.evolucao || []);
      } catch (err: any) {
        setError(
          err?.response?.data?.message || 'Erro ao carregar dados da visao geral.'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [municipioAtivo?.id, dataInicio, dataFim]);

  if (!municipioAtivo) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', px: 3 }}>
        <Typography variant="h5" color="text.secondary" gutterBottom>
          Nenhum municipio selecionado
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Selecione um municipio para visualizar o monitoramento.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        <Skeleton variant="text" width="40%" height={48} />
        <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2, mt: 2, mb: 3 }} />
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" height={350} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
      </Box>
    );
  }

  const topUnidades = data?.topUnidades || [];
  const kpis = data?.kpis || {};

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Monitoramento - Visao Geral
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            {municipioAtivo.nome}
          </Typography>
        </Box>
        <DateRangeSelector
          dataInicio={dataInicio}
          dataFim={dataFim}
          onChange={handleDateChange}
        />
      </Box>

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            titulo="Total de Atendimentos"
            valor={(kpis.total_atendimentos ?? 0).toLocaleString('pt-BR')}
            icone={<LocalHospitalIcon />}
            cor="#007AF0"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            titulo="Unidades Ativas"
            valor={(kpis.total_unidades ?? 0).toLocaleString('pt-BR')}
            icone={<BusinessIcon />}
            cor="#02786D"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            titulo="Especialidades"
            valor={(kpis.total_especialidades ?? 0).toLocaleString('pt-BR')}
            icone={<MedicalServicesIcon />}
            cor="#E76F00"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            titulo="Variacao no Periodo"
            valor={`${kpis.variacao_pct ?? 0}%`}
            variacao={kpis.variacao_pct}
            icone={<TrendingUpIcon />}
            cor="#6a1b9a"
          />
        </Grid>
      </Grid>

      {/* Top Unidades - Horizontal BarChart */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Top Unidades por Atendimentos
        </Typography>
        {topUnidades.length > 0 ? (
          <ResponsiveContainer width="100%" height={Math.max(topUnidades.length * 45, 250)}>
            <BarChart
              data={topUnidades}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tickFormatter={(v: number) => v.toLocaleString('pt-BR')} />
              <YAxis
                type="category"
                dataKey="nome_unidade"
                width={200}
                tick={{ fontSize: 12 }}
              />
              <Tooltip
                formatter={(value: any) => [
                  Number(value).toLocaleString('pt-BR'),
                  'Atendimentos',
                ]}
              />
              <Bar dataKey="total_atendimentos" fill="#007AF0" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Sem dados de unidades para o periodo selecionado.
          </Typography>
        )}
      </Paper>

      {/* Evolucao - LineChart */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Evolucao Mensal de Atendimentos
        </Typography>
        {evolucao.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={evolucao} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={(v: number) => v.toLocaleString('pt-BR')} />
              <Tooltip
                formatter={(value: any) => [
                  Number(value).toLocaleString('pt-BR'),
                  'Total',
                ]}
              />
              <Line
                type="monotone"
                dataKey="total"
                stroke="#007AF0"
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Sem dados de evolucao disponiveis.
          </Typography>
        )}
      </Paper>

      {/* Cards de navegacao para sub-paineis */}
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        Paineis Detalhados
      </Typography>
      <Grid container spacing={2}>
        {SUB_PANELS.map((panel) => (
          <Grid item xs={12} sm={6} md={3} key={panel.path}>
            <Paper
              elevation={1}
              onClick={() => navigate(panel.path)}
              sx={{
                p: 3,
                borderRadius: 2,
                cursor: 'pointer',
                borderTop: `3px solid ${panel.color}`,
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 4,
                },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1.5,
                textAlign: 'center',
              }}
            >
              <Box sx={{ color: panel.color }}>{panel.icon}</Box>
              <Typography variant="subtitle1" fontWeight={600}>
                {panel.label}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {municipioAtivo && data && (
        <AnalisePredicaoCard
          dadosContexto={JSON.stringify(data)}
          municipioId={municipioAtivo.id}
          painelId="monitoramento-dashboard"
          contexto="dashboard"
          manualTrigger
        />
      )}

      <ChatIAButton
        contexto="dashboard"
        dadosContexto={JSON.stringify(data)}
        fabSx={{ bottom: 24, right: 24, top: 'auto' }}
      />
    </Box>
  );
};

export default MonitoramentoDashboard;
